import type { ISnippetRule } from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { RuleDeletionAlert } from '../../RuleDeletionAlert';
import { RulesContent, type RulesContentListProps } from '../../RulesManager';
import type { CustomCodeManagerProps } from '../CustomCodeManager';
import { CustomCodeListItem } from './CustomCodeListItem';
import { CustomCodeSnippetPreview } from './CustomCodeSnippetPreview';

export interface CustomCodeContentProps
  extends Pick<
    CustomCodeManagerProps,
    'createSnippet' | 'updateSnippet' | 'deleteSnippet' | 'reorderSnippets'
  > {
  snippets: ISnippetRule[];
  isFiltersActive?: boolean;
}

export const CustomCodeContent = ({
  snippets,
  deleteSnippet,
  updateSnippet,
  reorderSnippets,
  isFiltersActive,
}: CustomCodeContentProps) => {
  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();
  const [snippetToDelete, setSnippetToDelete] = useState<ISnippetRule>(null);
  const [isDraggingAllowed, setIsDraggingAllowed] = useBoolean(true);

  const onReorderSnippets: RulesContentListProps<ISnippetRule>['onOrderChange'] =
    async ({ rules, destinationIndex }) => {
      const moved = rules[destinationIndex].rule;
      const after = rules[destinationIndex + 1]?.rule;
      await reorderSnippets(moved, after).catch(() => {
        notify.snackbar({
          type: 'error',
          label: 'Failed to reorder. Please try again',
        });
      });
    };
  return (
    <>
      <RulesContent.List<ISnippetRule>
        draggable={!isFiltersActive && isDraggingAllowed}
        rules={snippets.map((snippet) => ({
          id: snippet.ruleId,
          rule: snippet,
        }))}
        onOpenRule={setIsDraggingAllowed.setFalse}
        onCloseRule={setIsDraggingAllowed.setTrue}
        onOrderChange={onReorderSnippets}
        renderItem={({ ruleItem, open, openRule, closeRule }) => (
          <CustomCodeListItem
            snippets={snippets}
            snippet={ruleItem.rule}
            open={open}
            onDelete={(snippet) => {
              setSnippetToDelete(snippet);
              setShowDeletionAlert.setTrue();
            }}
            onCloseRule={closeRule}
            onOpenRule={openRule}
            onUpdate={async (snippet) => {
              try {
                await updateSnippet(snippet);
              } catch (_error) {
                notify.error('Snippet update failed.');
              }
            }}
          />
        )}
      />
      <RuleDeletionAlert
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        title="Delete snippet"
        onErrorMessage="Failed deleting snippet"
        onConfirm={async () => {
          await deleteSnippet(snippetToDelete);
          setShowDeletionAlert.setFalse();
        }}
        rulePreview={<CustomCodeSnippetPreview snippet={snippetToDelete} />}
      />
    </>
  );
};
